<script setup>
import { ref, onMounted, onBeforeUnmount, watch, Suspense } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { useColorMode } from '@vueuse/core';
import AppSidebar from './components/SideBar/AppSidebar.vue';
import { SidebarProvider, SidebarInset } from './components/ui/sidebar';
import DynamicBreadcrumb from './components/DynamicBreadcrumb.vue';
import { useFrontegg, useFronteggAuthGuard } from '@frontegg/vue';
import { useAuthStore } from './stores/auth.store';
import { WebSocketService, SentryService } from './services';
import _ from 'lodash';
import { useSourcesStore } from './stores/sources.store';
import { useDestinationsStore } from './stores/destinations.store';
import { usePipelinesStore } from './stores/pipelines.store';
import { useUserStore } from './stores/user.store';
import { useNovuStore } from './stores/novu.store';
import { useServicesStore } from './stores/services.store';
import { useLoaderStore } from './stores/loader.store';
import AppLoader from './components/AppLoader.vue';
import Config from './utils/config';
import { userHasAccess } from './utils/utils';
import { Permissions } from './utils/constants';
import hubspotService from './services/hubspot.service';
import GlobalDialogs from './components/dialogs/GlobalDialogs.vue';
import { Toaster } from './components/ui/sonner';
import 'vue-sonner/style.css';
import { useViewportHeight } from '@/composables/useViewportHeight';

// Track auth guard state
const authGuardEnabled = ref(false);

// Check if current route is in Frontegg zone
const isInFronteggZone = (path) => {
    return path.startsWith('/account/') || path.startsWith('/oauth/');
};

// Enable auth guard if not already enabled
const enableAuthGuardOnce = () => {
    if (!authGuardEnabled.value) {
        console.log('🔒 Frontegg auth guard activated');

        useFronteggAuthGuard();
        authGuardEnabled.value = true;
    }
};

// Initialize auth guard at top of setup - only once based on initial route
const shouldEnableAuthGuard = !isInFronteggZone(window.location.pathname);
if (shouldEnableAuthGuard) {
    enableAuthGuardOnce();
}

const colorMode = useColorMode();
useViewportHeight(); // Initialize dynamic viewport height
const router = useRouter();

const authStore = useAuthStore();
const sourcesStore = useSourcesStore();
const destinationsStore = useDestinationsStore();
const pipelinesStore = usePipelinesStore();
const userStore = useUserStore();
const novuStore = useNovuStore();
const servicesStore = useServicesStore();
const loaderStore = useLoaderStore();
const { fronteggLoaded, authState } = useFrontegg();

const firstLoad = ref(true);
const loading = ref(false);
const isPageBreadcrumbActive = ref(false);
const isPageFooterActive = ref(false);

authStore.updateState(authState);

const handleBeforeUnload = () => {
    WebSocketService.close();
};

const checkPageBreadcrumb = () => {
    const newValue = !!window.__pageBreadcrumbActive;
    if (isPageBreadcrumbActive.value !== newValue) {
        isPageBreadcrumbActive.value = newValue;
    }
};

const checkPageFooter = () => {
    const newValue = !!window.__pageFooterActive;
    if (isPageFooterActive.value !== newValue) {
        isPageFooterActive.value = newValue;
    }
};

async function preloadConnectorConfigurations() {
    try {
        await sourcesStore.fetchConnectors();
        await destinationsStore.fetchConnectors();
    } catch (error) {
        console.warn('Failed to preload connector information:', error);
    }
}

checkPageBreadcrumb();
checkPageFooter();

watch(isPageBreadcrumbActive, (newValue, oldValue) => {});

// Watch for route changes to enable auth guard when leaving Frontegg zone
watch(
    () => router.currentRoute.value.path,
    (newPath) => {
        if (!isInFronteggZone(newPath) && !authGuardEnabled.value && authState.isAuthenticated) {
            enableAuthGuardOnce();
        }
    }
);

// Watch for Frontegg auth state changes to enable guard when ready
watch(
    () => authState.isAuthenticated,
    (isAuthenticated) => {
        if (isAuthenticated && !authGuardEnabled.value && !isInFronteggZone(router.currentRoute.value.path)) {
            enableAuthGuardOnce();
        }
    }
);

const handlePageBreadcrumbChange = () => {
    checkPageBreadcrumb();
};

const handlePageFooterChange = () => {
    checkPageFooter();
};

window.addEventListener('pageBreadcrumbChanged', handlePageBreadcrumbChange);
window.addEventListener('pageFooterChanged', handlePageFooterChange);

setInterval(() => {
    checkPageBreadcrumb();
    checkPageFooter();
}, 100);

watch(
    colorMode,
    (newMode) => {
        if (newMode === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    },
    { immediate: true }
);

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    const hideChat = () => {
        const element = document.getElementById('hubspot-messages-iframe-container');
        if (element) {
            element.style.setProperty('display', 'none', 'important');
        }
        return element;
    };

    const interval = setInterval(() => {
        const element = hideChat();
        if (element && element.style.display === 'none') {
            clearInterval(interval);
        }
    }, 500);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('pageBreadcrumbChanged', handlePageBreadcrumbChange);
    window.removeEventListener('pageFooterChanged', handlePageFooterChange);
});

watch(
    () => [_.get(authState, 'user.accessToken', null), authState.isAuthenticated, authState.user],
    async ([newAccessToken, isAuthenticated, newUser], oldValues) => {
        const [oldAccessToken, oldIsAuthenticated, oldUser] = oldValues || [null, false, null];

        if (authStore.accessToken !== newAccessToken && newAccessToken) {
            pipelinesStore.pipelines = undefined;
            sourcesStore.sources = undefined;
            destinationsStore.destinations = undefined;
            userStore.tenants = undefined;
            firstLoad.value = true;
        }

        authStore.updateState(authState);

        // Debug: Log authState properties to understand 2FA states
        if (process.env.NODE_ENV === 'development') {
            console.log('Frontegg authState:', {
                isAuthenticated: authState?.isAuthenticated,
                isLoading: authState?.isLoading,
                fronteggLoaded,
                user: !!authState?.user,
                accessToken: !!authState?.user?.accessToken,
                profileData: !!authState?.user?.profileData,
                mfaRequired: authState?.user?.mfaRequired,
                requireMfa: authState?.user?.requireMfa,
                stepUpRequired: authState?.stepUpRequired,
                isSteppedUp: authState?.isSteppedUp,
                authenticationState: authState?.authenticationState,
                // App-specific states
                loading,
                firstLoad: firstLoad.value
            });
        }

        if (!oldIsAuthenticated && isAuthenticated) {
            firstLoad.value = true;
        } else if (oldIsAuthenticated && !isAuthenticated) {
            WebSocketService.close();
            firstLoad.value = true;
        }

        if (isAuthenticated && firstLoad.value && newUser) {
            try {
                firstLoad.value = false;
                loading.value = true;
                loaderStore.activateLoading('app.init');

                // Enable auth guard once user is authenticated (if not already enabled)
                enableAuthGuardOnce();

                try {
                    userStore.fetchTenantTree();
                } catch (e) {
                    console.warn('Tenant tree fetch failed:', e);
                }

                try {
                    window.pylon = {
                        chat_settings: {
                            app_id: 'baaa4911-2fd1-4c3a-bc59-ab905b8c63a4',
                            name: `${authStore.user.firstName} ${authStore.user.lastName}`,
                            email: authStore.user.email,
                            avatar_url: authStore.user.profilePictureUrl
                        }
                    };

                    window.pylon.debug = true;
                } catch (e) {
                    console.warn('Pylon chat setup failed:', e);
                }

                try {
                    window.analytics.identify(authStore.user.id, {
                        email: authStore.user.email,
                        name: `${authStore.user.firstName} ${authStore.user.lastName}`,
                        company: {
                            id: authState?.tenantsState?.activeTenant?.tenantId,
                            name: authState?.tenantsState?.activeTenant?.name
                        }
                    });
                } catch (e) {
                    console.warn('Analytics identify failed:', e);
                }

                try {
                    window.Appcues.identify(authStore.user.id, {
                        firstName: authStore.user.firstName,
                        lastName: authStore.user.lastName,
                        companyName: authState?.tenantsState?.activeTenant?.name,
                        email: authStore.user.email
                    });
                } catch (e) {
                    console.warn('Appcues identify failed:', e);
                }

                if (userHasAccess([Permissions.READ_SERVICES], authStore.user)) {
                    try {
                        servicesStore.fetch();
                    } catch (e) {
                        console.warn('Services fetch failed:', e);
                    }
                }

                try {
                    preloadConnectorConfigurations();
                } catch (e) {
                    console.warn('Preload connector configurations failed:', e);
                }

                try {
                    await novuStore.initializeService();
                } catch (e) {
                    console.warn('Novu service init failed:', e);
                }

                await Promise.allSettled([
                    Promise.resolve(SentryService.init()).catch((e) => console.warn('Sentry init failed:', e)),
                    Promise.resolve(WebSocketService.init()).catch((e) => console.warn('WebSocket init failed:', e))
                ]);

                if (!Config.app.isDev) {
                    try {
                        hubspotService.init();
                    } catch (e) {
                        console.warn('Hubspot init failed:', e);
                    }
                }
            } catch (error) {
                console.error('Error during app initialization:', error);
                loaderStore.deactivateLoading('app.init');
            } finally {
                firstLoad.value = false;
                loading.value = false;
                loaderStore.deactivateLoading('app.init');
            }
        }
    },
    { immediate: true }
);

const handleRouteError = (error) => {
    console.error('Route component error:', error);
};
</script>

<template>
    <SidebarProvider
        v-if="
            fronteggLoaded &&
            authState?.isAuthenticated &&
            authState?.user?.accessToken &&
            !authState?.isLoading &&
            !loading &&
            !firstLoad &&
            !isInFronteggZone(router.currentRoute.value.path)
        "
        id="app"
        class="h-screen relative">
        <AppSidebar />

        <SidebarInset class="min-h-0 flex flex-col overflow-hidden">
            <header class="flex h-12 shrink-0 items-center justify-between gap-2 bg-background px-4 border-b border-border">
                <div class="flex items-center h-12 shrink-0 py-4">
                    <div id="breadcrumb-container">
                        <DynamicBreadcrumb v-if="!isPageBreadcrumbActive" />
                    </div>
                </div>
                <div
                    id="page-breadcrumb-actions-slot"
                    class="flex items-center gap-3"
                    :class="{ 'opacity-0 pointer-events-none': !isPageBreadcrumbActive }"></div>
            </header>

            <main class="flex-1 overflow-auto p-4">
                <RouterView v-slot="{ Component, route: currentRoute }">
                    <AppLoader :force-loading="firstLoad || loading" default-height="100%">
                        <Suspense @error="handleRouteError">
                            <template #default>
                                <component v-if="!loading && Component" :is="Component" :key="currentRoute.fullPath" />
                            </template>
                            <template #fallback>
                                <div class="flex items-center justify-center h-64">
                                    <div class="text-sm text-muted-foreground">Loading...</div>
                                </div>
                            </template>
                        </Suspense>
                    </AppLoader>
                </RouterView>
            </main>

            <footer class="shrink-0 border-t border-border bg-background" :class="{ 'opacity-0 pointer-events-none': !isPageFooterActive }">
                <div id="page-footer-slot"></div>
            </footer>
        </SidebarInset>
    </SidebarProvider>

    <!-- Loading state when authenticated but app not fully loaded -->
    <div
        v-else-if="fronteggLoaded && authState?.isAuthenticated && (authState?.isLoading || loading || firstLoad || !authState?.user?.accessToken)"
        class="h-screen flex items-center justify-center bg-background">
        <div class="text-center">
            <AppLoader :force-loading="true" class="min-h-20" />
            <p class="mt-4 text-muted-foreground">
                {{ authState?.isLoading ? 'Completing authentication...' : 'Loading your workspace...' }}
            </p>
        </div>
    </div>

    <Toaster
        class="pointer-events-auto"
        position="top-right"
        :theme="colorMode === 'dark' ? 'dark' : 'light'"
        :rich-colors="true"
        :close-button="true" />

    <GlobalDialogs />
</template>
